import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AtomField from "./AtomField";
import { useAuth } from "../../contexts/AuthContext";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { ADULT_AGE_RANGES } from "../../utils/ageEligibility";
import "./AgeGate.css";

const AgeGate = ({ onConfirm, user }) => {
  const navigate = useNavigate();
  const { authFetch, refreshUser } = useAuth();
  // Every branch below renders the same full-screen overlay (there's no
  // "return null" path) — mounted at all only means it's showing, so this
  // must run before any of the early returns (rules of hooks).
  useBodyScrollLock(true);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // A logged-in account that self-declared "Under 18" gets no bypass at
  // all — the server already refuses this content for them regardless
  // (backend/app/main.py's _is_age_verified_adult), so a control here
  // would just be a confusing dead end.
  if (user?.age_range === "Under 18") {
    return (
      <div className="age-gate-overlay" data-lenis-prevent>
        <AtomField />
        <div className="age-gate-modal">
          <span className="age-gate-icon" aria-hidden="true">🍷</span>
          <h2>Not Available</h2>
          <p>This section isn't available for accounts registered as under 18.</p>
          <div className="age-gate-actions">
            <button type="button" className="age-gate-no" onClick={() => navigate("/")}>Take me back</button>
          </div>
        </div>
      </div>
    );
  }

  // Anonymous — nothing to declare; only a real account can be verified.
  // Most age-restricted categories also set requires_auth, which already
  // redirects an anonymous visitor to /login before this ever renders
  // (see CategoryPage.jsx) — this stays as a defensive fallback in case a
  // future age-restricted surface doesn't also set that flag.
  if (!user) {
    return (
      <div className="age-gate-overlay" data-lenis-prevent>
        <AtomField />
        <div className="age-gate-modal">
          <span className="age-gate-icon" aria-hidden="true">🍷</span>
          <h2>Sign In Required</h2>
          <p>This section contains alcoholic beverages. Sign in and confirm you're 18+ to continue.</p>
          <div className="age-gate-actions">
            <button
              type="button"
              className="age-gate-yes"
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            >
              Sign In
            </button>
            <button type="button" className="age-gate-no" onClick={() => navigate("/")}>Take me back</button>
          </div>
        </div>
      </div>
    );
  }

  const confirm = async () => {
    if (!selected) { setError("Please select your age range."); return; }
    setSaving(true);
    setError("");
    try {
      await authFetch("/users/me", { method: "PATCH", body: { age_range: selected } });
      await refreshUser();
      onConfirm();
    } catch {
      setError("Couldn't save that — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="age-gate-overlay" data-lenis-prevent>
      <AtomField />
      <div className="age-gate-modal">
        <span className="age-gate-icon" aria-hidden="true">🍷</span>
        <h2>Confirm Your Age</h2>
        <p>This section contains alcoholic beverages. Confirm your age range to continue.</p>
        <label className="age-gate-label" htmlFor="age-gate-range">Age range</label>
        <select
          id="age-gate-range"
          className="age-gate-select"
          value={selected}
          onChange={(e) => { setSelected(e.target.value); setError(""); }}
        >
          <option value="">— Select —</option>
          {ADULT_AGE_RANGES.map((range) => (
            <option key={range} value={range}>{range}</option>
          ))}
        </select>
        {error && <p className="age-gate-error" role="alert">{error}</p>}
        <div className="age-gate-actions">
          <button type="button" className="age-gate-yes" onClick={confirm} disabled={saving}>
            {saving ? "Saving…" : "Confirm"}
          </button>
          <button type="button" className="age-gate-no" onClick={() => navigate("/")}>No, take me back</button>
        </div>
      </div>
    </div>
  );
};

export default AgeGate;
